// @givmo/org-console — the class-mismatch forward, built once and dark by construction (OM-6).
//
// ══ THE SITUATION ══════════════════════════════════════════════════════════════════════════════
//
// One Givmo identity can administer organizations of different KINDS, and each kind has its own
// console. Someone who signs in at the wrong one today reaches a considered "this account doesn't
// administer one of these" screen — true, and a dead end, when the console they wanted is one host
// away and they already hold a membership there.
//
// This hook is the forward. A host mounts it in its OWN no-access branch and says where it can send
// a caller whose memberships it cannot render.
//
// ══ WHEN IT FIRES, WORDED PRECISELY ════════════════════════════════════════════════════════════
//
// **After any session establishment (interactive or adopted), at access resolution** — never "at
// sign-in". A silently-adopted suite session takes the identical downstream road as a typed
// password, re-entering the same auth chokepoint, so hooking the ACCESS branch rather than the
// credential path covers both with no extra wiring. Hooking it in a credential package would cover
// neither correctly: membership shape is only knowable after the read.
//
// ══ IT FAILS TOWARD STAYING, IN EVERY DIRECTION ════════════════════════════════════════════════
//
// A forward is a positive act on a positive answer, and everything else leaves the caller exactly
// where they were:
//
//   * the route ABSENT (its dark state — and today, its only state: the backend has no such route
//     yet) → stay. Absence is "no answer", never "no memberships";
//   * the read ERRORED → stay;
//   * an EMPTY list → stay. This person genuinely administers nothing anywhere;
//   * a membership in a class this host DOES serve → stay. It never runs for them anyway: a host
//     that could render the caller's organization is not in its own no-access branch. Its own class
//     is simply absent from {@link MismatchForwardOptions.destinations};
//   * a class the host named but has NO destination configured for → stay. Undefaulted env-config
//     means no link rather than a guessed host, and that rule does not soften because the link
//     happens to be a redirect;
//   * `enabled: false` → no request at all.
//
// There is no client flag. The read's own dark gate IS the reveal switch: while the route is absent
// every branch above resolves to "stay", so this ships inert and starts working the day the backend
// serves it. A second switch would be a second thing to remember to flip.
//
// ⚠ AND IT NAMES NO CLASS. `entity_class` values arrive as strings the HOST supplies, matched
// against strings the WIRE supplies; nothing here branches on what any of them mean. That is the
// same seam the rest of this package holds — a client that could reason about the kind of
// organization it is judged under is a client picking its own authorization rules.

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { decodeOrgMemberships } from './org-decode';
import { fetchOrgMemberships, isOrgConsoleUnavailable } from './org-console-transport';
import type { OrgMembershipRow } from './wire-types';

/** The cache key for the caller's own memberships. Not scoped by organization — see the wire type. */
export const ORG_MEMBERSHIPS_KEY = ['org-console', 'memberships'] as const;

export interface MismatchForwardOptions {
  /**
   * Read only while this is true.
   *
   * ⚠ A HOST PASSES ITS OWN NO-ACCESS BRANCH, and that branch must be a POSITIVE resolution — "the
   * read succeeded and carried no organization" — never an inference from a failed one. Forwarding
   * someone off a console because a request timed out is the one wrong answer this must never give.
   */
  enabled: boolean;
  /**
   * Where to send a caller holding a membership this host cannot render, keyed by `entity_class`.
   *
   * A class ABSENT from this map is never forwarded — which is how a host declares its OWN class
   * without this package ever learning what that is. A class present with `null` is one the host
   * knows about but has no configured destination for in this build: still never forwarded, because
   * an unset destination means no link rather than a guessed host.
   */
  destinations: Readonly<Record<string, string | null>>;
}

export interface MismatchForward {
  /** A forward has been decided and the document is leaving. Hosts may keep rendering; it is brief. */
  isForwarding: boolean;
  /** Every membership the read reported, or `[]` while it has reported nothing. */
  memberships: OrgMembershipRow[];
}

/**
 * The first membership this host cannot render but CAN send somewhere, with its destination.
 *
 * Pure and exported so the decision is testable without a router, a fetch or a document. First rather
 * than "best": the wire's order is the backend's, no client-side preference among another class's
 * organizations would be honest, and the destination is that console's ROOT, where its own landing
 * rule takes over and picks properly.
 */
export function forwardTargetFor(
  memberships: readonly OrgMembershipRow[],
  destinations: Readonly<Record<string, string | null>>,
): string | null {
  for (const membership of memberships) {
    const destination = destinations[membership.entity_class];
    if (typeof destination === 'string' && destination.trim().length > 0) {
      return destination.trim();
    }
  }
  return null;
}

/**
 * Forward a caller this host cannot serve to a console that can. See the header for every branch.
 */
export function useMismatchForward(options: MismatchForwardOptions): MismatchForward {
  const { enabled, destinations } = options;

  const query = useQuery({
    queryKey: ORG_MEMBERSHIPS_KEY,
    enabled,
    queryFn: async ({ signal }) => decodeOrgMemberships(await fetchOrgMemberships({ signal })),
    // Absence is the answer while the route is dark, and it will not change on a second ask.
    retry: (failureCount, error: Error) => !isOrgConsoleUnavailable(error) && failureCount < 1,
    // The same structural defence `useOrgSelfAccess` carries: this observer is mounted CONDITIONALLY
    // on another read's state, which is exactly the shape that turned a retry-on-mount into an
    // unbounded loop once already.
    retryOnMount: false,
  });

  const memberships = query.data ?? [];
  const target = query.isSuccess ? forwardTargetFor(memberships, destinations) : null;

  useEffect(() => {
    if (target === null) return;
    // A whole-document replace, not a push: the screen behind this is a dead end, and leaving it in
    // history means Back returns to it and forwards again.
    window.location.replace(target);
  }, [target]);

  return { isForwarding: target !== null, memberships };
}
